import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, ShieldCheck, UserPlus } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel, Empty } from "../components/Widgets";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

export const isOwner = (u) => u?.role === "admin" && u?.admin_level === "owner";
export const lastSeen = (iso) => (iso ? new Date(iso).toLocaleString() : "Never");

function ChangePasscode() {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.next !== f.confirm) return toast.error("New passcodes do not match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-passcode", { current_passcode: f.current, new_passcode: f.next });
      if (data.token) localStorage.setItem("rc_token", data.token);
      toast.success("Passcode updated"); setF({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const field = (k, label, tid) => (
    <div><div className="text-xs text-slate-400 mb-1.5">{label}</div>
      <Input data-testid={tid} type="password" required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="bg-[#0B0F17]" /></div>
  );
  return (
    <Panel title="Change passcode">
      <form onSubmit={submit} className="p-5 space-y-4 max-w-md" data-testid="change-passcode-form">
        {field("current", "Current passcode", "current-passcode-input")}
        {field("next", "New passcode (min 6 characters)", "new-passcode-input")}
        {field("confirm", "Confirm new passcode", "confirm-passcode-input")}
        <Button data-testid="change-passcode-submit" disabled={busy} className="bg-blue-500 hover:bg-blue-600 text-white">{busy ? "Saving…" : "Update passcode"}</Button>
      </form>
    </Panel>
  );
}

function RecoveryEmail({ initial }) {
  const { setUser } = useAuth();
  const [v, setV] = useState(initial || "");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.put("/auth/recovery-email", { recovery_email: v.trim() || null });
      setV(data.recovery_email || "");
      setUser((u) => ({ ...u, recovery_email: data.recovery_email }));
      toast.success(data.recovery_email ? "Backup email saved" : "Backup email removed");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Backup reset email" className="mt-6">
      <form onSubmit={save} className="p-5 max-w-md space-y-3" data-testid="recovery-email-form">
        <div className="text-xs text-slate-400">Passcode reset links go to your sign-in email and this backup address. Leave blank to remove it.</div>
        <Input data-testid="recovery-email-input" type="email" placeholder="backup@gmail.com" value={v} onChange={(e) => setV(e.target.value)} className="bg-[#0B0F17]" />
        <Button data-testid="recovery-email-save" disabled={busy} className="bg-blue-500 hover:bg-blue-600 text-white">{busy ? "Saving…" : "Save backup email"}</Button>
      </form>
    </Panel>
  );
}

function AdminsPanel() {
  const [admins, setAdmins] = useState([]);
  const [f, setF] = useState({ name: "", email: "", passcode: "" });
  const load = () => api.get("/admins").then((r) => setAdmins(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admins", f); toast.success("Secondary admin added"); setF({ name: "", email: "", passcode: "" }); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Remove admin access for ${a.name}?`)) return;
    try { await api.delete(`/admins/${a.id}`); toast.success(`${a.name} removed`); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Panel title="Admins" className="mt-6" action={<span className="text-xs text-slate-500">secondary admins can view & edit, but cannot delete data</span>}>
      <table className="tbl w-full" data-testid="admins-table">
        <thead><tr><th>Name</th><th>Email</th><th>Access</th><th>Last sign-in</th><th /></tr></thead>
        <tbody>{admins.map((a) => (
          <tr key={a.id} data-testid={`admin-row-${a.email}`}>
            <td>{a.name}</td><td className="font-mono text-xs text-slate-400">{a.email}</td>
            <td>{a.admin_level === "owner"
              ? <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border text-emerald-400 border-emerald-500/40"><ShieldCheck size={10} className="inline mr-1" />Main admin</span>
              : <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border text-blue-300 border-blue-500/40">Secondary</span>}</td>
            <td className="font-mono text-xs text-slate-400">{lastSeen(a.last_login_at)}</td>
            <td className="text-right">{a.admin_level !== "owner" && <button data-testid={`remove-admin-${a.email}`} onClick={() => remove(a)} className="text-slate-500 hover:text-rose-400"><Trash2 size={15} /></button>}</td>
          </tr>
        ))}</tbody>
      </table>
      <form onSubmit={add} className="p-5 grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end border-t border-slate-800" data-testid="add-admin-form">
        <Input data-testid="admin-name-input" required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-[#0B0F17]" />
        <Input data-testid="admin-email-input" required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-[#0B0F17]" />
        <Input data-testid="admin-passcode-input" required type="password" placeholder="Passcode (min 6)" value={f.passcode} onChange={(e) => setF({ ...f, passcode: e.target.value })} className="bg-[#0B0F17]" />
        <Button data-testid="add-admin-button" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950"><UserPlus size={14} className="mr-2" />Add admin</Button>
      </form>
    </Panel>
  );
}

export default function Account() {
  const { user } = useAuth();
  return (
    <div>
      <PageHeader eyebrow="Settings" title="Account" />
      <Panel className="mb-6">
        <div className="p-5 text-sm" data-testid="account-summary">
          <div className="font-medium">{user.name}</div>
          <div className="font-mono text-xs text-slate-400">{user.email}</div>
          <div className="text-xs text-slate-500 mt-2">{user.role === "agent" ? "Agent account" : isOwner(user) ? "Main admin — full access" : "Secondary admin — can view and edit, cannot delete data"}</div>
        </div>
      </Panel>
      <ChangePasscode />
      {user.role === "admin" && <RecoveryEmail initial={user.recovery_email} />}
      {isOwner(user) ? <AdminsPanel /> : user.role === "admin" && <Panel className="mt-6"><Empty>Only the main admin can manage admin accounts.</Empty></Panel>}
    </div>
  );
}
